import { useLocale } from "./use-locale.ts";

export function useTranslation<T extends { language: string }>(
  { translations }: { translations: T[] },
  property: Exclude<keyof T, "language">,
): string | undefined {
  const currentLanguage = useCurrentLanguage();

  if (!translations) {
    throw new Error("Translations not supplied");
  }

  const translation = translations.find(
    (it) => it.language === currentLanguage,
  )?.[property];

  const fallback = translations.find((it) => it.language === "EN")?.[property];

  if (!translation) {
    console.warn(`Missing translation for ${currentLanguage}`);
  }

  if (!fallback) {
    console.error(`Missing fallback for ${JSON.stringify(translations)}`);
  }

  return (translation ?? fallback) as string | undefined;
}

/**
 * The short text of an event in the fan's language, or null.
 *
 * It does not fall back to EN. A tile without a short text shows the regular
 * text, and `useTranslation` gives that its EN fallback. So the order is the
 * short text, then the text, then the EN text (ADR-018).
 */
export function useShortTranslation({
  translations,
}: {
  translations: { language: string; textShort: string | null }[];
}): string | null {
  const currentLanguage = useCurrentLanguage();

  return (
    translations.find((it) => it.language === currentLanguage)?.textShort ??
    null
  );
}

function useCurrentLanguage() {
  return useLocale().split("-")[0].toUpperCase();
}
